/**
 * Customer-facing copy for Data Hub run failures.
 *
 * Raw server text ("Internal server error", SQL fragments, stack dumps) must
 * not appear next to a failed step. Product-authored messages that are already
 * plain language stay as-is.
 */

const PHASE_LABELS: Record<string, string> = {
  import: "Import",
  cleanse: "QC",
  commit: "Promote",
  benchmark: "Benchmark",
  normalize: "Normalize",
  summarize: "Summarize",
  kpi_scores: "Category Scores",
  ro_scores: "R&O Scores",
};

/** Patterns that read as infrastructure / engine dumps, not product copy. */
const OPAQUE_ERROR =
  /internal server error|server error\b|status code|econnrefused|failed to fetch|network\s*error|traceback|exception\b|syntax for type|relation does not|undefined is not|typeerror|referenceerror|ENOENT|ECONN|at\s+\S+\s+\(|http\/\d/i;

export function phaseDisplayName(phase: string | null | undefined): string {
  if (!phase) return "This step";
  return PHASE_LABELS[phase] ?? phase;
}

/**
 * True when the message is already safe to show a customer (short product
 * copy). Opaque / technical dumps return false.
 */
export function isCustomerSafeRunError(
  message: string | null | undefined
): boolean {
  const raw = (message ?? "").trim();
  if (!raw) return false;
  if (OPAQUE_ERROR.test(raw)) return false;
  // Long dumps and JSON blobs are never product copy.
  if (raw.length > 180) return false;
  if (raw.startsWith("{") || raw.startsWith("[")) return false;
  return true;
}

export type CustomerFacingRunErrorOpts = {
  phase?: string | null;
  raw?: string | null;
  /** One-line for the outcomes list; fuller copy for the modal. */
  variant?: "short" | "full";
};

/**
 * Message for the customer UI. Keeps technical detail out of view.
 */
export function customerFacingRunError(
  opts: CustomerFacingRunErrorOpts
): string {
  const raw = (opts.raw ?? "").trim();
  if (raw && isCustomerSafeRunError(raw)) return raw;

  if (opts.variant === "short") {
    return "The process could not be completed. Your data has been preserved. Please try again.";
  }

  // Modal uses a matching headline; this is the supporting detail only.
  return "Your data has been preserved. Please try again. If the issue persists, contact support for assistance.";
}
